import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Search, X } from 'lucide-react';
import { AcademicClassesApi, Section, SectionsApi } from '@/services/academic.api';
import { AttendanceApi, StaffPerson } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Input, Select } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { DataTable, useDataTable } from '@/components/data-table';

const columnHelper = createColumnHelper<Section>();

function errMsg(e: unknown): string {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong. Please try again.';
}

/**
 * A minimal "Search Teacher Name" picker (Image 5) — no reusable combobox
 * component exists in the UI kit yet, so this is scoped locally rather
 * than adding one for a single use. Type to filter, click a name to
 * select; the current selection shows as the input's placeholder text
 * (kept, not cleared, so it's visible without needing to search again).
 */
function TeacherPicker({
  teachers,
  value,
  onChange,
  disabled,
}: {
  teachers: StaffPerson[];
  value: string | null;
  onChange: (teacherId: string) => void;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const currentName = teachers.find((t) => t.id === value)?.name;
  const filtered = teachers.filter((t) => t.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div ref={boxRef} className="relative w-40">
      <Input
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={currentName ?? 'Search teacher name…'}
        disabled={disabled}
        className="!py-1.5 text-sm"
      />
      {open && (
        <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400">No matching teacher.</div>
          ) : (
            filtered.map((t) => (
              <button
                key={t.id}
                type="button"
                className="block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50"
                onClick={() => {
                  onChange(t.id);
                  setQuery('');
                  setOpen(false);
                }}
              >
                {t.name}
                <span className="ml-1.5 text-xs text-slate-400">({t.role})</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/** Class teacher + per-subject teacher assignment for one section, shown inside the manage modal. */
function SectionTeacherManager({ sectionId }: { sectionId: string }) {
  const qc = useQueryClient();
  const [movedFrom, setMovedFrom] = useState<string[] | null>(null);

  const { data } = useQuery({
    queryKey: ['sections', sectionId, 'teachers'],
    queryFn: () => SectionsApi.getTeachers(sectionId),
  });
  const { data: teachers } = useQuery({
    queryKey: ['attendance', 'staff-people'],
    queryFn: () => AttendanceApi.staffPeople(),
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['sections', sectionId, 'teachers'] });
    qc.invalidateQueries({ queryKey: ['sections', 'list'] });
  }

  const setClassTeacher = useMutation({
    mutationFn: (teacherId: string) => SectionsApi.setClassTeacher(sectionId, teacherId),
    onSuccess: (r) => {
      // A teacher can only be class-teacher of one section — the backend
      // already cleared their old slot(s); surface what moved.
      setMovedFrom(r.previousSections.length ? r.previousSections.map((s) => s.name) : null);
      invalidate();
    },
  });
  const clearClassTeacher = useMutation({
    mutationFn: () => SectionsApi.setClassTeacher(sectionId, null),
    onSuccess: invalidate,
  });
  const assignSubjectTeacher = useMutation({
    mutationFn: (vars: { subjectId: string; teacherId: string }) => SectionsApi.assignSubjectTeacher(sectionId, vars),
    onSuccess: invalidate,
    // Not surfaced as a page-wide error — shown inline per-subject below,
    // since a duplicate-assignment 409 only makes sense in the context of
    // the specific subject row it happened on.
  });
  const unassignSubjectTeacher = useMutation({
    mutationFn: (subjectTeacherId: string) => SectionsApi.unassignSubjectTeacher(sectionId, subjectTeacherId),
    onSuccess: invalidate,
  });

  if (!data || !teachers) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div className="space-y-4">
      <div>
        <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Class Teacher</h4>
        <div className="flex items-center gap-2">
          <TeacherPicker
            teachers={teachers}
            value={data.classTeacherId}
            onChange={(teacherId) => setClassTeacher.mutate(teacherId)}
            disabled={setClassTeacher.isPending}
          />
          {data.classTeacherId && (
            <button
              type="button"
              title="Unassign"
              className="rounded p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
              onClick={() => clearClassTeacher.mutate()}
              disabled={clearClassTeacher.isPending}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {(setClassTeacher.error || clearClassTeacher.error) && (
          <p className="mt-1 text-xs text-red-600">{errMsg(setClassTeacher.error ?? clearClassTeacher.error)}</p>
        )}
        {movedFrom && (
          <p className="mt-1 text-xs text-amber-600">Also removed as class teacher of {movedFrom.join(', ')}.</p>
        )}
      </div>

      <div>
        <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Subject Teachers <span className="font-normal normal-case text-slate-400">(more than one teacher per subject is allowed)</span>
        </h4>
        {data.subjects.length === 0 ? (
          <p className="text-xs text-slate-400">No subjects assigned to this class yet — use Class Subject management first.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.subjects.map((subj) => {
              const assignedIds = new Set(subj.teachers.map((t) => t.teacherId));
              const availableTeachers = teachers.filter((t) => !assignedIds.has(t.id));
              const rowError =
                assignSubjectTeacher.variables?.subjectId === subj.subjectId ? assignSubjectTeacher.error : undefined;
              return (
                <li key={subj.subjectId} className="py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="w-32 shrink-0 text-sm font-medium text-slate-700">{subj.subjectName}</span>
                    {subj.teachers.map((t) => (
                      <span
                        key={t.subjectTeacherId}
                        className="inline-flex items-center gap-1 rounded bg-slate-100 py-0.5 pl-2 pr-1 text-xs text-slate-700"
                      >
                        {t.teacherName}
                        <button
                          type="button"
                          title="Unassign"
                          className="rounded p-0.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
                          onClick={() => unassignSubjectTeacher.mutate(t.subjectTeacherId)}
                          disabled={unassignSubjectTeacher.isPending}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                    <TeacherPicker
                      teachers={availableTeachers}
                      value={null}
                      onChange={(teacherId) => assignSubjectTeacher.mutate({ subjectId: subj.subjectId, teacherId })}
                      disabled={assignSubjectTeacher.isPending}
                    />
                  </div>
                  {rowError && <p className="mt-1 text-xs text-red-600">{errMsg(rowError)}</p>}
                </li>
              );
            })}
          </ul>
        )}
        {unassignSubjectTeacher.error && (
          <p className="mt-1 text-xs text-red-600">{errMsg(unassignSubjectTeacher.error)}</p>
        )}
      </div>
    </div>
  );
}

/**
 * Extends the existing `academic/sections` module — no old class-section
 * write path was live to disable (surveyed first: no `/school/sections`
 * controller is registered anywhere; the frontend's dead SectionsApi in
 * school.api.ts would 404, not corrupt data). See docs/DEVIATIONS.md.
 */
export function SectionTeachersPage() {
  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'name', defaultSortOrder: 'asc' });
  const [classId, setClassId] = useState('');
  const [managing, setManaging] = useState<Section | null>(null);

  const { data: classes } = useQuery({
    queryKey: ['academic-classes', 'all-for-section-teacher-filter'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });

  const { data, isFetching } = useQuery({
    queryKey: ['sections', 'list', dt.params, classId],
    queryFn: () => SectionsApi.list({ ...dt.params, classId: classId || undefined }),
    placeholderData: keepPreviousData,
  });

  const columns = [
    columnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    columnHelper.accessor('className', { header: 'Class', cell: (info) => info.getValue() ?? '—' }),
    columnHelper.accessor('name', { header: 'Section' }),
    columnHelper.accessor('classTeacherName', {
      header: 'Class Teacher',
      cell: (info) => info.getValue() ?? <span className="text-xs uppercase tracking-wider text-amber-600">Not Assigned</span>,
    }),
    columnHelper.accessor('subjectTeachers', {
      header: 'Subject Teacher',
      cell: (info) => {
        const rows = info.getValue() ?? [];
        if (!rows.length) return <span className="text-xs uppercase tracking-wider text-amber-600">Not Assigned</span>;
        return (
          <ol className="space-y-0.5">
            {rows.map((r, i) => (
              <li key={i} className="text-sm">
                {i + 1}. {r.subjectName} - {r.teacherName ?? <span className="text-amber-600">Not Assigned</span>}
              </li>
            ))}
          </ol>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Action',
      cell: (info) => (
        <button className="btn-secondary !py-1 text-xs" onClick={() => setManaging(info.row.original)}>
          Manage
        </button>
      ),
    }),
  ];

  return (
    <div>
      <PageHeader
        title="Class Section & Teachers"
        description="Assign a class teacher and a subject teacher per section."
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={dt.searchInput}
            onChange={(e) => dt.setSearchInput(e.target.value)}
            placeholder="Search section name…"
            className="pl-9"
          />
        </div>
        <Select className="!w-44" value={classId} onChange={(e) => setClassId(e.target.value)}>
          <option value="">All Classes</option>
          {(classes?.items ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
      </div>

      <DataTable
        columns={columns}
        result={data}
        state={dt}
        loading={isFetching}
        emptyMessage="No sections found."
        getRowId={(row) => row.id}
      />

      <Modal
        open={!!managing}
        onClose={() => setManaging(null)}
        title={managing ? `${managing.className ?? ''} — ${managing.name}` : 'Section'}
        size="lg"
      >
        {managing && <SectionTeacherManager sectionId={managing.id} />}
      </Modal>
    </div>
  );
}
